import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonHTMLAttributes, ReactNode, useContext } from 'react';

import ChipContainer from './ChipContainer';
import ChipContext, { ChipVariant } from './context/ChipContext';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
    isSelected?: boolean;
    /** 라벨 왼쪽 아이콘 슬롯. 16px 기준으로 라벨과 gap 4 를 둔다. */
    icon?: ReactNode;
}

function Chip(props: Props) {
    const {
        isSelected = false, icon, children, ...rest
    } = props;
    const { variant = 'fill' } = useContext(ChipContext);
    return (
        <Base type='button'
            aria-label='chip'
            selected={isSelected}
            chipVariant={variant}
            {...rest} >
            {icon}
            {children}
        </Base>
    );
}

const SELECTED_STYLES: Record<ChipVariant, ReturnType<typeof css>> = {
    fill: css`
        background-color: var(--color-primary);
        border-color: transparent;
        color: var(--color-button-text);
        font-weight: bold;
    `,
    outline: css`
        font-weight: bold;
    `,
};

const STYLE_PROPS = ['selected', 'chipVariant'];

const Base = styled('button', {
    shouldForwardProp: (prop) => !STYLE_PROPS.includes(prop),
})<{
    selected: boolean;
    chipVariant: ChipVariant;
}>`
    align-items: center;
    background-color: var(--color-background-pure);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-m);
    color: var(--color-text-secondary);
    column-gap: var(--spacing-1);
    display: inline-flex;
    flex-shrink: 0;
    font-size: var(--font-size-14);
    font-weight: 400;
    min-width: 56px;
    height: 40px;
    justify-content: center;
    line-height: 1.4;
    min-height: 40px;
    padding: var(--spacing-2);
    white-space: nowrap;
    ${({ selected, chipVariant }) => (selected ? SELECTED_STYLES[chipVariant] : undefined)}
`;

Chip.Container = ChipContainer;

export default Chip;
